import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { AssessmentService } from 'src/app/modules/evaluations/assessment/services/assessment.services';
import { ReferenceValueResponse } from 'src/app/modules/evaluations/interfaces/reason-for-delay';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { SVGIcon, plusIcon } from '@progress/kendo-svg-icons';
import { ReferenceStore } from 'src/app/stores/reference.store';
import {
  EI_EXCLUDED_REASON_IDS,
  PRE_SCHOOL_ALLOWED_REASON_IDS,
} from '../../constants/reason-for-delays-contants';
import { combineLatest, first, takeUntil } from 'rxjs';
import { DocumentService } from 'src/app/services/document.service';
import { StudentDocument } from '../../interfaces/student-document';
import { AddReasonForDelayRequest } from './reason-for-delay-models/add-reason-for-delay.model';
import { AuthStore } from 'src/app/stores/auth.store';
import { User } from '../../models/user';
import { getReferenceDataById } from '../../functions/get-reference-data-by-id';
import { NotificationService } from '@progress/kendo-angular-notification';
import { NOTIFICATION_MESSAGES } from '../../constants/notification-messages';
import { ApiResponse } from '../../interfaces/api-response';
import { ReasonForDelay } from './interface/reason-for-delay';
import { AbstractComponent } from '../abstract-component';
import { DocumentType } from '../../enums/document-types';

@Component({
  selector: 'app-reason-for-delay',
  templateUrl: './reason-for-delay.component.html',
  styleUrls: ['./reason-for-delay.component.scss'],
  standalone: false,
})
export class ReasonForDelayComponent
  extends AbstractComponent
  implements OnInit, OnDestroy
{
  @Input() referralsk: string;
  @Input() studentDetails: any;
  @Input() studentSk: string;
  @Input() nycId: string;
  @Input() eiId: string;
  @Input() isPreSchool: boolean;
  @Input() studentStatus: string;
  @Input() canEdit: boolean;
  @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() saveEmitter: EventEmitter<void> = new EventEmitter<void>();

  form: FormGroup;
  referenceData: Record<string, IReferenceData[]>;
  reasonForDelay: IReferenceData | null = null;
  cpseUser: boolean = false;
  loadSpinner: boolean = true;
  svgPlus: SVGIcon = plusIcon;
  removeConfirmDialogOpen: boolean = false;
  closeConfirmDialogOpen: boolean = false;
  private pendingRemoveIndex: number | null = null;

  private user: User;
  private readonly referralStage: number = 1;
  private readonly caseClosedStatus: string = 'Closed';
  private pendingDocumentDeletions: number[] = [];

  constructor(
    private fb: FormBuilder,
    private referenceStore: ReferenceStore,
    private authStore: AuthStore,
    private assessmentService: AssessmentService<any>,
    private notificationService: NotificationService,
    private documentService: DocumentService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.form = this.fb.group({ reasons: this.fb.array([]) });

    this.authStore.userInfo$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((user: User) => {
        this.user = user;
        this.cpseUser = user?.isGlobalUser && user?.isSupervisor;
      });

    combineLatest([
      this.referenceStore.reasonForDelay$,
      this.assessmentService.getReasonDelay(this.referralsk, false),
    ])
      .pipe(first(), takeUntil(this.componentDestroyed))
      .subscribe(
        ([reasonForDelay, existingReasons]: [
          IReferenceData[],
          ReferenceValueResponse[],
        ]) => {
          this.referenceData = {
            reasonForDelay: this.filterReasonForDelayOptions(reasonForDelay),
          };

          (existingReasons ?? []).forEach((reason: any) => {
            const fg: FormGroup = this.createReasonForDelayFormGroup();
            fg.patchValue({
              reasonForDelay: getReferenceDataById(
                reason.id,
                this.referenceData.reasonForDelay,
              ),
              description: reason.description,
              referralSk: this.referralsk,
              stageId: reason.stageId,
              addedBy: reason.lastModuser,
              dateAdded: reason.lastModTs,
              isActive: reason.isActive,
              isNew: false,
              files: [],
              existingDocuments: reason.documents ?? [],
            });
            this.reasons.push(fg);
          });

          this.loadSpinner = false;
        },
      );
  }

  onDeleteDocument(doc: StudentDocument, reasonCtrl: AbstractControl): void {
    const existing: StudentDocument[] =
      reasonCtrl.get('existingDocuments')?.value ?? [];
    reasonCtrl
      .get('existingDocuments')
      ?.setValue(
        existing.filter(
          (d: StudentDocument) => d.documentRef !== doc.documentRef,
        ),
      );
  }

  get reasons(): FormArray {
    return this.form?.get('reasons') as FormArray;
  }

  get reasonsControls(): AbstractControl[] {
    return this.reasons?.controls ?? [];
  }

  get hasActiveReasons(): boolean {
    return this.reasonsControls.some(
      (ctrl: AbstractControl) => ctrl.get('isActive')?.value === true,
    );
  }

  get activeReasonsCount(): number {
    return this.reasonsControls.filter(
      (ctrl: AbstractControl) => ctrl.get('isActive')?.value === true,
    ).length;
  }

  // Used by the top combobox to populate the list of reasons available to add.
  // Filters out all already-selected reasons since there is no existing entry to preserve.
  get availableReasons(): IReferenceData[] {
    return this.getAvailableReasonsForEntry(null);
  }

  // Returns the list of reasons filtered to prevent duplicates, while keeping the current entry's own reason visible.
  // Used by the dropdown inside each expansion panel so the user cannot pick an already-selected reason,
  // but can still see and change the reason currently assigned to that entry.
  getAvailableReasonsForEntry(
    currentReasonId: number | string | null,
  ): IReferenceData[] {
    const selectedIds: number[] = this.reasonsControls
      .filter((ctrl: AbstractControl) => ctrl.get('isActive')?.value === true)
      .map((ctrl: AbstractControl) =>
        Number(ctrl.get('reasonForDelay')?.value?.id),
      );

    return (this.referenceData?.reasonForDelay ?? []).filter(
      (option: IReferenceData) =>
        !selectedIds.includes(Number(option.id)) ||
        Number(option.id) === Number(currentReasonId),
    );
  }

  createReasonForDelayFormGroup(): FormGroup {
    return this.fb.group({
      reasonForDelay: ['', Validators.required],
      description: [''],
      referralSk: [this.referralsk],
      stageId: [this.referralStage],
      addedBy: [this.user?.username],
      dateAdded: [],
      isActive: [],
      isNew: [false], // track whether the entry is new or existing, only new entries will be sent to the backend on save
      files: [[]],
      existingDocuments: [[]],
      expanded: [false],
    });
  }

  addReasonForDelay(): void {
    const fg: FormGroup = this.createReasonForDelayFormGroup();
    fg.patchValue({
      reasonForDelay: this.reasonForDelay,
      dateAdded: new Date(),
      isActive: true,
      isNew: true,
      expanded: true,
    });
    this.reasons.push(fg);
    fg.markAsDirty(); // new entries must always be sent to the backend
    this.reasonForDelay = null; // reset the combobox after adding the reason to the list
  }

  onRemoveClick(index: number): void {
    this.pendingRemoveIndex = index;
    this.removeConfirmDialogOpen = true;
  }

  confirmRemove(): void {
    if (this.pendingRemoveIndex !== null) {
      this.removeReasonForDelay(this.pendingRemoveIndex);
    }
    this.removeConfirmDialogOpen = false;
    this.pendingRemoveIndex = null;
  }

  cancelRemove(): void {
    this.removeConfirmDialogOpen = false;
    this.pendingRemoveIndex = null;
  }

  removeReasonForDelay(index: number): void {
    const fg: FormGroup = this.reasons.at(index) as FormGroup;

    if (fg.get('isNew')?.value) {
      // Never saved to the backend — discard entirely
      this.reasons.removeAt(index);
    } else {
      // If the reason for delay entry exists in the backend, mark it as inactive.
      // Queue associated documents for deletion — they will only be deleted on a successful save.
      const docs: StudentDocument[] = fg.get('existingDocuments')?.value ?? [];
      docs.forEach((doc: StudentDocument) =>
        this.pendingDocumentDeletions.push(doc.documentRef),
      );

      // Mark inactive so the backend deactivates it on save
      fg.patchValue({ isActive: false });
      fg.markAsDirty();
    }
  }

  save(): void {
    this.loadSpinner = true;
    this.submitReasons();
  }

  private get hasFilesToUpload(): boolean {
    return this.reasons.controls
      .filter((ctrl: AbstractControl) => ctrl.dirty)
      .some(
        (ctrl: AbstractControl) => (ctrl.get('files')?.value ?? []).length > 0,
      );
  }

  private getPayloadUploadDocuments(): FormData {
    const formData: FormData = new FormData();
    this.reasons.controls
      .filter((ctrl: AbstractControl) => ctrl.dirty)
      .forEach((ctrl: AbstractControl) => {
        const files: { rawFile?: File }[] = ctrl.get('files')?.value ?? [];
        const reasonForDelayId: string = ctrl
          .get('reasonForDelay')
          ?.value?.id?.toString();
        files.forEach((fileObj: { rawFile?: File }) => {
          formData.append('id', this.nycId);
          formData.append('studentSk', this.studentSk);
          formData.append(
            'file',
            fileObj.rawFile ?? (fileObj as unknown as File),
          );
          formData.append('shpPanel', reasonForDelayId);
          formData.append('documentType', DocumentType.ReasonForDelay);
          formData.append('caseActionSk', this.referralsk);
          formData.append('comment', ctrl.get('description')?.value ?? '');
        });
      });
    return formData;
  }

  private submitReasons(): void {
    const requests: AddReasonForDelayRequest[] = this.reasons.controls
      .filter((control: AbstractControl) => control.dirty)
      .map(
        (control: AbstractControl) =>
          new AddReasonForDelayRequest(control.value, this.referenceData),
      );
    this.assessmentService
      .submitReasonForDelay(this.referralsk, requests)
      .subscribe((response: ApiResponse<ReasonForDelay[]>) => {
        // If the reasons were not saved successfully, show an error notification and do not proceed with uploading documents or closing the dialog, even if there are files to upload.
        // The user needs to fix the issues first before the documents can be associated with the reasons.
        if (!response.isRequestSucessful) {
          this.loadSpinner = false;
          response.errorCodes.forEach((error: Record<string, string>) => {
            this.errorNotification(error.value);
          });
        } else if (this.hasFilesToUpload) {
          // Reasons saved successfully, now upload any associated documents. If document upload fails, show an error notification.
          // The reasons will still be saved, but the user will need to re-upload the documents after fixing the issues.
          this.documentService
            .uploadDocument(this.getPayloadUploadDocuments())
            .subscribe({
              next: () => {
                this.reasons.controls.forEach((ctrl: AbstractControl) =>
                  ctrl.get('files')?.setValue([]),
                );
                this.onSaveSuccess();
              },
              error: (error: string) => {
                this.errorNotification(error);
              },
            });
        } else {
          this.onSaveSuccess();
        }
      });
  }

  private errorNotification(error: string): void {
    this.loadSpinner = false;
    this.notificationService.show({
      content: error,
      cssClass: 'notification-error',
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'slide', duration: 60 },
      type: { style: 'error', icon: true },
    });
  }

  private onSaveSuccess(): void {
    this.loadSpinner = false;
    this.successNotification();
    this.deleteDocuemnts();
    this.closeAfterSave();
  }

  private deleteDocuemnts(): void {
    this.pendingDocumentDeletions.forEach((ref: number) =>
      this.documentService.deleteDocument(ref, 'deleted').subscribe(),
    );
    this.pendingDocumentDeletions = [];
  }

  private successNotification(): void {
    this.notificationService.show({
      content: NOTIFICATION_MESSAGES.saveSuccess,
      cssClass: 'notification-success',
      hideAfter: 5000,
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'slide', duration: 200 },
      type: { style: 'success', icon: true },
    });
  }

  onCloseClick(): void {
    if (this.form?.dirty) {
      this.closeConfirmDialogOpen = true;
    } else {
      this.closeDialog();
    }
  }

  confirmClose(): void {
    this.closeConfirmDialogOpen = false;
    this.closeDialog();
  }

  cancelClose(): void {
    this.closeConfirmDialogOpen = false;
  }

  closeDialog(): void {
    this.close.emit(false);
  }

  // Close the dialog after the user saved parent information
  closeAfterSave(): void {
    this.closeDialog();
    this.saveEmitter.next();
  }

  filterReasonForDelayOptions(
    reasonForDelayOptions: IReferenceData[],
  ): IReferenceData[] {
    if (!this.studentDetails) return reasonForDelayOptions;

    if (this.eiId) {
      return reasonForDelayOptions.filter(
        (option: IReferenceData) =>
          !EI_EXCLUDED_REASON_IDS.includes(Number(option.id)),
      );
    }

    if (this.eiId === null && this.isPreSchool) {
      return reasonForDelayOptions.filter((option: IReferenceData) =>
        PRE_SCHOOL_ALLOWED_REASON_IDS.includes(Number(option.id)),
      );
    }

    return reasonForDelayOptions;
  }

  override afterOnDestroy(): void {
    //
  }
}
